import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import { Refresh, Add, School, MonetizationOn, Groups, WorkspacePremium, PersonAdd } from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Cell } from 'recharts';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import hrPlusService from '../../services/hrPlusService';
import hrService, { EmployeeListItem } from '../../services/hrService';

// ---------------------------------------------------------------------------
// Types — mirror HrTalent.js column names on the backend. The service layer
// (hrPlusService) is untyped (Promise<any>), so these interfaces are what
// keep this page honest about what the API actually returns.
// ---------------------------------------------------------------------------

type TrainingStatus = 'PLANNED' | 'SCHEDULED' | 'ONGOING' | 'COMPLETED' | 'CANCELLED';
interface Training {
  id: number;
  name: string;
  category: string | null;
  trainer: string | null;
  mode: string | null;
  start_date: string | null;
  end_date: string | null;
  cost: number | string | null;
  capacity: number | null;
  status: TrainingStatus;
  enrolled_count?: number | string;
}

type EnrollmentStatus = 'ENROLLED' | 'ATTENDING' | 'COMPLETED' | 'DROPPED';
interface Enrollment {
  id: number;
  training_id: number;
  employee_id: number;
  employee_name: string;
  department_id: number | null;
  status: EnrollmentStatus;
  completion_date: string | null;
  score: number | string | null;
  certified: number | boolean | null;
  created_at: string;
}

interface TrainingDetail extends Training {
  enrollments: Enrollment[];
}

interface TrainingDashboard {
  completionRatePct: number | null;
  enrollmentsByStatus: Record<string, number>;
  totalTrainingCost: number;
  byCategory: { category: string; count: number }[];
}

interface SkillMatrixRow {
  skillName: string;
  employeeCount: number;
  avgCurrentLevel: number | null;
}

interface SkillGapRow {
  employeeId: number;
  employeeName: string;
  skillName: string;
  currentLevel: number;
  targetLevel: number;
  gap: number;
}

interface SkillMatrix {
  bySkill: SkillMatrixRow[];
  gaps: SkillGapRow[];
}

const num = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const trainingStatuses: TrainingStatus[] = ['PLANNED', 'SCHEDULED', 'ONGOING', 'COMPLETED', 'CANCELLED'];
const trainingStatusTone: Record<TrainingStatus, StatusTone> = {
  PLANNED: 'neutral',
  SCHEDULED: 'info',
  ONGOING: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const enrollmentStatuses: EnrollmentStatus[] = ['ENROLLED', 'ATTENDING', 'COMPLETED', 'DROPPED'];
const enrollmentStatusTone: Record<EnrollmentStatus, StatusTone> = {
  ENROLLED: 'neutral',
  ATTENDING: 'info',
  COMPLETED: 'success',
  DROPPED: 'error',
};

const modeOptions = ['ONLINE', 'OFFLINE', 'HYBRID'];

// ---------------------------------------------------------------------------
// Small local building blocks
// ---------------------------------------------------------------------------

const SectionCard: React.FC<{ title: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  subtitle,
  action,
  children,
}) => (
  <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: subtitle ? 0.25 : 1.5, gap: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {action}
    </Box>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

const SectionHeader: React.FC<{ title: string; onRefresh: () => void; busy: boolean; action?: React.ReactNode }> = ({
  title,
  onRefresh,
  busy,
  action,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="Refresh">
        <IconButton onClick={onRefresh} disabled={busy}>
          <Refresh />
        </IconButton>
      </Tooltip>
      {action}
    </Box>
  </Box>
);

/** Small proportion bar for a level-out-of-5 style metric, matching StatCard's share bar language. */
const LevelBar: React.FC<{ value: number | null; max?: number; color: string }> = ({ value, max = 5, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
    <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: `${color}1f`, overflow: 'hidden' }}>
      <Box
        sx={{
          width: value !== null ? `${Math.min(Math.max((value / max) * 100, 0), 100)}%` : 0,
          height: '100%',
          borderRadius: 3,
          backgroundColor: color,
        }}
      />
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 28 }}>
      {value !== null ? value.toFixed(1) : '—'}
    </Typography>
  </Box>
);

type TabKey = 'trainings' | 'skills';

const LearningPage: React.FC = () => {
  const t = useChartTokens();
  const [tab, setTab] = useState<TabKey>('trainings');

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);

  const [trainings, setTrainings] = useState<Training[]>([]);
  const [trainingsLoading, setTrainingsLoading] = useState(true);
  const [trainingsError, setTrainingsError] = useState<string | null>(null);

  const [dashboard, setDashboard] = useState<TrainingDashboard | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const [skillMatrix, setSkillMatrix] = useState<SkillMatrix | null>(null);
  const [skillMatrixLoading, setSkillMatrixLoading] = useState(true);
  const [skillMatrixError, setSkillMatrixError] = useState<string | null>(null);

  const [trainingStatusUpdating, setTrainingStatusUpdating] = useState<number | null>(null);

  // ---- new training dialog ----
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [tName, setTName] = useState('');
  const [tCategory, setTCategory] = useState('');
  const [tTrainer, setTTrainer] = useState('');
  const [tMode, setTMode] = useState('');
  const [tStartDate, setTStartDate] = useState('');
  const [tEndDate, setTEndDate] = useState('');
  const [tCost, setTCost] = useState('');
  const [tCapacity, setTCapacity] = useState('');
  const [trainingBusy, setTrainingBusy] = useState(false);
  const [trainingFormError, setTrainingFormError] = useState<string | null>(null);

  // ---- training detail dialog ----
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<TrainingDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [enrollEmployeeId, setEnrollEmployeeId] = useState<number | ''>('');
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrollmentUpdating, setEnrollmentUpdating] = useState<number | null>(null);

  const loadTrainings = () => {
    setTrainingsLoading(true);
    setTrainingsError(null);
    hrPlusService
      .getTrainings()
      .then((res: { trainings: Training[] }) => setTrainings(res.trainings))
      .catch((err: any) => setTrainingsError(err?.message || 'Failed to load trainings'))
      .finally(() => setTrainingsLoading(false));
  };

  const loadDashboard = () => {
    setDashboardLoading(true);
    setDashboardError(null);
    hrPlusService
      .trainingDashboard()
      .then((res: TrainingDashboard) => setDashboard(res))
      .catch((err: any) => setDashboardError(err?.message || 'Failed to load training dashboard'))
      .finally(() => setDashboardLoading(false));
  };

  const loadSkillMatrix = () => {
    setSkillMatrixLoading(true);
    setSkillMatrixError(null);
    hrPlusService
      .skillMatrix()
      .then((res: SkillMatrix) => setSkillMatrix(res))
      .catch((err: any) => setSkillMatrixError(err?.message || 'Failed to load skill matrix'))
      .finally(() => setSkillMatrixLoading(false));
  };

  useEffect(() => {
    loadTrainings();
    loadDashboard();
    loadSkillMatrix();
    hrService
      .getEmployees({ status: 'ACTIVE', limit: 200 })
      .then((res) => setEmployees(res.employees))
      .catch(() => undefined);
  }, []);

  const employeeLabel = (e: EmployeeListItem) => `${e.employee_code} — ${e.first_name}${e.last_name ? ` ${e.last_name}` : ''}`;

  const totalEnrollments = useMemo(() => {
    if (!dashboard) return 0;
    return Object.values(dashboard.enrollmentsByStatus).reduce((s, v) => s + v, 0);
  }, [dashboard]);

  // ---- new training ----
  const openTrainingDialog = () => {
    setTName('');
    setTCategory('');
    setTTrainer('');
    setTMode('');
    setTStartDate('');
    setTEndDate('');
    setTCost('');
    setTCapacity('');
    setTrainingFormError(null);
    setTrainingDialogOpen(true);
  };

  const handleCreateTraining = async () => {
    if (!tName.trim()) {
      setTrainingFormError('Name is required');
      return;
    }
    setTrainingBusy(true);
    setTrainingFormError(null);
    try {
      await hrPlusService.createTraining({
        name: tName,
        category: tCategory || undefined,
        trainer: tTrainer || undefined,
        mode: tMode || undefined,
        startDate: tStartDate || undefined,
        endDate: tEndDate || undefined,
        cost: tCost ? Number(tCost) : undefined,
        capacity: tCapacity ? Number(tCapacity) : undefined,
      });
      setTrainingDialogOpen(false);
      loadTrainings();
      loadDashboard();
    } catch (err: any) {
      setTrainingFormError(err?.message || 'Failed to create training');
    } finally {
      setTrainingBusy(false);
    }
  };

  const handleTrainingStatusChange = async (training: Training, e: SelectChangeEvent) => {
    const status = e.target.value as TrainingStatus;
    if (status === training.status) return;
    setTrainingStatusUpdating(training.id);
    try {
      const res: { training: Training } = await hrPlusService.setTrainingStatus(training.id, status);
      setTrainings((prev) => prev.map((tr) => (tr.id === training.id ? { ...tr, ...res.training } : tr)));
      if (detail && detail.id === training.id) {
        setDetail((prev) => (prev ? { ...prev, status: res.training.status } : prev));
      }
    } catch (err: any) {
      setTrainingsError(err?.message || 'Failed to update training status');
    } finally {
      setTrainingStatusUpdating(null);
    }
  };

  // ---- detail dialog ----
  const openDetail = (id: number) => {
    setDetailId(id);
    setDetail(null);
    setDetailError(null);
    setEnrollEmployeeId('');
    setEnrollError(null);
    setDetailLoading(true);
    hrPlusService
      .getTraining(id)
      .then((res: { training: TrainingDetail }) => setDetail(res.training))
      .catch((err: any) => setDetailError(err?.message || 'Failed to load training detail'))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  const refreshDetail = () => {
    if (detailId !== null) openDetail(detailId);
  };

  const handleEnroll = async () => {
    if (!detailId || !enrollEmployeeId) {
      setEnrollError('Select an employee to enroll');
      return;
    }
    setEnrollBusy(true);
    setEnrollError(null);
    try {
      await hrPlusService.enroll(detailId, enrollEmployeeId);
      setEnrollEmployeeId('');
      refreshDetail();
      loadTrainings();
    } catch (err: any) {
      setEnrollError(err?.message || 'Failed to enroll employee');
    } finally {
      setEnrollBusy(false);
    }
  };

  const handleMarkCompleted = async (enrollment: Enrollment) => {
    setEnrollmentUpdating(enrollment.id);
    try {
      await hrPlusService.setEnrollmentStatus(enrollment.id, 'COMPLETED');
      refreshDetail();
      loadDashboard();
    } catch (err: any) {
      setDetailError(err?.message || 'Failed to update enrollment');
    } finally {
      setEnrollmentUpdating(null);
    }
  };

  return (
    <Box>
      <PageHeader title="Learning &amp; Development" subtitle="Trainings, enrollments and the organisation's skill matrix" />

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab value="trainings" label="Trainings" />
        <Tab value="skills" label="Skill Matrix" />
      </Tabs>

      {/* ---------------------------------------------------------------- */}
      {/* TRAININGS                                                        */}
      {/* ---------------------------------------------------------------- */}
      {tab === 'trainings' && (
        <Box>
          {dashboardError && !dashboardLoading ? (
            <ErrorState message={dashboardError} onRetry={loadDashboard} />
          ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Completion Rate"
                  value={dashboardLoading ? null : dashboard?.completionRatePct != null ? `${dashboard.completionRatePct}%` : null}
                  icon={<WorkspacePremium />}
                  color="#059669"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Training Cost"
                  value={dashboardLoading ? null : dashboard ? formatIndianNumber(dashboard.totalTrainingCost) : null}
                  icon={<MonetizationOn />}
                  color="#0284c7"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="Total Enrollments" value={dashboardLoading ? null : totalEnrollments} icon={<Groups />} color="#7c3aed" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="Trainings" value={trainingsLoading ? null : trainings.length} icon={<School />} color="#d97706" />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={12}>
              <SectionCard title="Trainings by Category" subtitle="Count of trainings scheduled per category">
                {!dashboard || dashboard.byCategory.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No training data yet.
                  </Typography>
                ) : (
                  <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dashboard.byCategory} margin={{ top: 12, right: 8, left: -6, bottom: 4 }}>
                        <CartesianGrid {...gridProps(t)} vertical={false} />
                        <XAxis dataKey="category" {...axisProps(t)} />
                        <YAxis {...axisProps(t)} width={40} allowDecimals={false} />
                        <RTooltip {...tooltipStyle(t)} formatter={(v: number) => [v, 'Trainings']} />
                        <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={44}>
                          {dashboard.byCategory.map((_c, i) => (
                            <Cell key={i} fill={t.series(i)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <SectionHeader
            title="Trainings"
            onRefresh={() => {
              loadTrainings();
              loadDashboard();
            }}
            busy={trainingsLoading}
            action={
              <Button variant="contained" size="small" startIcon={<Add />} onClick={openTrainingDialog}>
                New Training
              </Button>
            }
          />

          {trainingsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTrainingsError(null)}>
              {trainingsError}
            </Alert>
          )}

          <TableSurface>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Name', 'Category', 'Trainer', 'Mode', 'Dates', 'Cost', 'Capacity', 'Enrolled', 'Status'].map((h, i) => (
                      <TableCell key={h} align={i === 5 || i === 6 || i === 7 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trainingsLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6, border: 0 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : trainings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ border: 0 }}>
                        <TableEmptyState title="No trainings yet" hint="Create one to start enrolling employees." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    trainings.map((tr) => (
                      <TableRow key={tr.id} sx={bodyRowSx} hover onClick={() => openDetail(tr.id)} style={{ cursor: 'pointer' }}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tr.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{tr.category ? <TypePill label={tr.category} tint={t.categorical[0]} /> : <Typography variant="body2">—</Typography>}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{tr.trainer || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{tr.mode || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {fmtDate(tr.start_date)} – {fmtDate(tr.end_date)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}
                            title={num(tr.cost) !== null ? formatIndianNumber(num(tr.cost) as number) : undefined}
                          >
                            {num(tr.cost) !== null ? formatIndianNumber(num(tr.cost) as number) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {tr.capacity ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {tr.enrolled_count ?? 0}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StatusDot label={tr.status} tone={trainingStatusTone[tr.status]} />
                            <Select
                              size="small"
                              value={tr.status}
                              onChange={(e) => handleTrainingStatusChange(tr, e)}
                              disabled={trainingStatusUpdating === tr.id}
                              sx={{ minWidth: 140, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' } }}
                            >
                              {trainingStatuses.map((s) => (
                                <MenuItem key={s} value={s}>
                                  Move to {s}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* SKILL MATRIX                                                     */}
      {/* ---------------------------------------------------------------- */}
      {tab === 'skills' && (
        <Box>
          {skillMatrixError && !skillMatrixLoading ? (
            <ErrorState message={skillMatrixError} onRetry={loadSkillMatrix} />
          ) : (
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard title="Skills" subtitle="Employees with this skill recorded, and the average current level">
                  <TableSurface>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {['Skill', 'Employees', 'Avg. Level'].map((h, i) => (
                              <TableCell key={h} align={i === 1 ? 'right' : 'left'} sx={headerCellSx}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {skillMatrixLoading ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center" sx={{ py: 6, border: 0 }}>
                                <CircularProgress size={28} />
                              </TableCell>
                            </TableRow>
                          ) : !skillMatrix || skillMatrix.bySkill.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ border: 0 }}>
                                <TableEmptyState title="No skills recorded yet" />
                              </TableCell>
                            </TableRow>
                          ) : (
                            skillMatrix.bySkill.map((s) => (
                              <TableRow key={s.skillName} sx={bodyRowSx}>
                                <TableCell sx={{ py: 1.25 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {s.skillName}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {s.employeeCount}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <LevelBar value={s.avgCurrentLevel} color={t.categorical[0]} />
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </TableSurface>
                </SectionCard>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard title="Average Level by Skill" subtitle="Scale 1–5, across all employees with the skill recorded">
                  {!skillMatrix || skillMatrix.bySkill.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No skills recorded yet.
                    </Typography>
                  ) : (
                    <Box sx={{ height: Math.max(200, skillMatrix.bySkill.length * 36) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={skillMatrix.bySkill} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
                          <CartesianGrid {...gridProps(t)} horizontal={false} />
                          <XAxis type="number" domain={[0, 5]} {...axisProps(t)} />
                          <YAxis type="category" dataKey="skillName" {...axisProps(t)} width={110} tick={{ fill: t.textSecondary, fontSize: 11 }} />
                          <RTooltip {...tooltipStyle(t)} formatter={(v: number) => [Number(v).toFixed(1), 'Avg level']} />
                          <Bar dataKey="avgCurrentLevel" radius={[0, 3, 3, 0]} maxBarSize={20}>
                            {skillMatrix.bySkill.map((_s, i) => (
                              <Cell key={i} fill={t.series(i)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </SectionCard>
              </Grid>

              <Grid size={12}>
                <SectionCard title="Skill Gaps" subtitle="Employees whose current level is below their target level, largest gap first">
                  <TableSurface>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {['Employee', 'Skill', 'Current', 'Target', 'Gap'].map((h, i) => (
                              <TableCell key={h} align={i >= 2 ? 'right' : 'left'} sx={headerCellSx}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {skillMatrixLoading ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 6, border: 0 }}>
                                <CircularProgress size={28} />
                              </TableCell>
                            </TableRow>
                          ) : !skillMatrix || skillMatrix.gaps.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ border: 0 }}>
                                <TableEmptyState title="No skill gaps" hint="Every recorded skill meets or exceeds its target level." />
                              </TableCell>
                            </TableRow>
                          ) : (
                            skillMatrix.gaps.map((g, i) => (
                              <TableRow key={`${g.employeeId}-${g.skillName}-${i}`} sx={bodyRowSx}>
                                <TableCell sx={{ py: 1.25 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {g.employeeName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{g.skillName}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {g.currentLevel}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {g.targetLevel}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <StatusDot label={`−${g.gap}`} tone={g.gap >= 2 ? 'error' : 'warning'} />
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </TableSurface>
                </SectionCard>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* New training dialog */}
      <Dialog open={trainingDialogOpen} onClose={() => (trainingBusy ? undefined : setTrainingDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New Training</DialogTitle>
        <DialogContent dividers>
          {trainingFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {trainingFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Name" required value={tName} onChange={(e) => setTName(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" label="Category" value={tCategory} onChange={(e) => setTCategory(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" label="Trainer" value={tTrainer} onChange={(e) => setTTrainer(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField select fullWidth size="small" label="Mode" value={tMode} onChange={(e) => setTMode(e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {modeOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Capacity"
                type="number"
                value={tCapacity}
                onChange={(e) => setTCapacity(e.target.value)}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={tStartDate}
                onChange={(e) => setTStartDate(e.target.value)}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={tEndDate}
                onChange={(e) => setTEndDate(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Cost (₹)" type="number" value={tCost} onChange={(e) => setTCost(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrainingDialogOpen(false)} disabled={trainingBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateTraining} disabled={trainingBusy}>
            {trainingBusy ? 'Creating…' : 'Create Training'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Training detail dialog */}
      <Dialog open={detailId !== null} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.name || 'Training Detail'}</DialogTitle>
        <DialogContent dividers>
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          {detailLoading && !detail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : detail ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {detail.category || '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Trainer
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {detail.trainer || '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Dates
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fmtDate(detail.start_date)} – {fmtDate(detail.end_date)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Cost / Capacity
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {num(detail.cost) !== null ? formatIndianNumber(num(detail.cost) as number) : '—'} / {detail.capacity ?? '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Enrollments ({detail.enrollments.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    select
                    size="small"
                    label="Enroll Employee"
                    value={enrollEmployeeId}
                    onChange={(e) => setEnrollEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {employees.map((e) => (
                      <MenuItem key={e.id} value={e.id}>
                        {employeeLabel(e)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button size="small" variant="contained" startIcon={<PersonAdd />} onClick={handleEnroll} disabled={enrollBusy}>
                    {enrollBusy ? 'Enrolling…' : 'Enroll'}
                  </Button>
                </Box>
              </Box>

              {enrollError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEnrollError(null)}>
                  {enrollError}
                </Alert>
              )}

              <TableSurface>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Employee', 'Status', 'Completion Date', 'Score', 'Certified', ''].map((h, i) => (
                          <TableCell key={h || `col-${i}`} align={i === 3 ? 'right' : 'left'} sx={headerCellSx}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.enrollments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ border: 0 }}>
                            <TableEmptyState title="No enrollments yet" hint="Enroll an employee above." />
                          </TableCell>
                        </TableRow>
                      ) : (
                        detail.enrollments.map((en) => (
                          <TableRow key={en.id} sx={bodyRowSx}>
                            <TableCell sx={{ py: 1.25 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {en.employee_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <StatusDot label={en.status} tone={enrollmentStatusTone[en.status]} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{fmtDate(en.completion_date)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {num(en.score) !== null ? num(en.score) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{en.certified ? 'Yes' : 'No'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {en.status !== 'COMPLETED' && (
                                <Button size="small" disabled={enrollmentUpdating === en.id} onClick={() => handleMarkCompleted(en)}>
                                  {enrollmentUpdating === en.id ? 'Saving…' : 'Mark Completed'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableSurface>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearningPage;
